import { generateContactFromUserStatusAndRing } from "@/lib/chat.helper"
import { chatStore$ } from "@/lib/ContactsMap"
import { type TRing, UserStatusSchema } from "@/schemas/user.schema"
import { syncStore$ } from "@/stores/sync.v2.store"
import { fetchMapKeyData } from "@/sync/sync"
import type { IRegisterDocumentProps } from "@/sync/syncTypes"
import consola from "consola"

export const UserStatusDocumentHandler = (name: string): IRegisterDocumentProps => ({
  name,
  onFailure: (error: unknown) => {
    consola.error(`Document ${name} failed`, error)
  },
  onRemoved: () => {
    consola.debug(`Document removed: ${name}`)
  },
  onDocumentUpdated: async (data: unknown, rawData: unknown) => {
    const parsedItem = UserStatusSchema.safeParse(data)
    if (!parsedItem.success) {
      consola.error(`We will ignore the update from ${name} because the data is invalid`, parsedItem.error)
      return
    }

    syncStore$.userStatus.set(parsedItem.data)
    consola.log(`Updated state from ${name}`, parsedItem.data)

    if (parsedItem.data.phoneStatus === "AVAILABLE" || parsedItem.data.phoneStatus === "NO_ANSWER_COOLDOWN") {
      // It is not a live call, we just need to check if this user has a live call
      // and if it does, we clear it
      // IMPORTANT: Currently we are not able to know from the sync object which contact
      // was in the call, so we clear all live calls
      // If in the future we want to support multiple live calls at the same time, we need to
      // change this logic

      chatStore$.liveCalls.set([])
      return
    }

    // It is a live call
    const ringData: TRing | undefined = parsedItem.data.instaCall ?? parsedItem.data.ring ?? undefined

    if (!ringData) {
      // There is no ring data, ignore this update
      return
    }

    // Before adding the live call, we need to check if it is a new contact
    const isNewContact = chatStore$.contacts[`${ringData.other!.id!}`].get() === undefined

    // If it is a new contact (for example, if it is from an incoming call)
    // we need to get its data from the server
    if (isNewContact) {
      consola.debug("Received a new contact", ringData.other!.id!)

      generateContactFromUserStatusAndRing(`${ringData.other!.id!}`, parsedItem.data, ringData)

      consola.debug("Queued fetching contact data from the sync...")
      fetchMapKeyData({
        key: `${ringData.other!.id!}`,
        map: "contacts",
      })
    }
    //

    // If this contact is not set as a live call contact
    if (chatStore$.liveCalls.get().findIndex((call) => call.contactId === `${ringData.other!.id!}`) === -1) {
      // Add it at the top
      chatStore$.liveCalls.unshift({
        contactId: `${ringData.other!.id!}`,
        ringId: `${ringData.id}`,
        status: parsedItem.data.phoneStatus,
        direction: ringData.direction,
      })
    }

    // If the contact is already in the live calls list, we need to update its status
    if (chatStore$.liveCalls.get().findIndex((call) => call.contactId === `${ringData.other!.id!}`) !== -1) {
      chatStore$.liveCalls.set(chatStore$.liveCalls.get().map((call) => (call.contactId === `${ringData.other!.id!}` ? { ...call, status: parsedItem.data.phoneStatus } : call)))
    }
    // Next thing is to check if this contact is already in the chat list
    if (!chatStore$._internal.chatsMap[`${ringData.other!.id!}`].peek()) {
      // It should be added to the chat list but at the top (index 0)
      // since it is a live call (happening now!)
      chatStore$.chats.unshift(`${ringData.other!.id!}`)
      chatStore$._internal.chatsMap[`${ringData.other!.id!}`].set(true)
    } else {
      // If it is, we need to move it to the top
      const arrWithoutContact = chatStore$.chats.peek().filter((contactID) => contactID !== `${ringData.other!.id!}`)
      chatStore$.chats.set([`${ringData.other!.id!}`, ...arrWithoutContact])
    }
  },
})
